import type { Color } from '@lib/colors/colors'
import { colors } from '@lib/colors/colors'
import { useEffect, useRef } from 'react'

export interface AccentCardProps {
  barColor?: Color
  duration?: number
  height?: number
  lineWidth?: number
  percentage: number
  primaryFontSize?: number
  primaryText?: string
  secondaryFontSize?: number
  secondaryText?: string
  startingPercentage?: number
  valueColor?: Color
  width?: number
}

export default function Tachometer({
  barColor = 'neutral100',
  duration = 1000,
  height = 200,
  lineWidth = 15,
  percentage = 0,
  primaryFontSize = 24,
  primaryText,
  secondaryFontSize = 14,
  secondaryText,
  startingPercentage = 0,
  valueColor = 'primary500',
  width = 200,
}: AccentCardProps) {
  let ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    // ensure to load fonts before drawing canvas
    document.fonts.ready.then(function () {
      let canvas = ref.current
      if (!canvas) return
      let ctx = canvas.getContext('2d')
      if (!ctx) return

      // ensure the canvas is looking good on retina
      let ratio = window.devicePixelRatio
      let rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * ratio
      canvas.height = rect.height * ratio
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`

      const start = performance.now()
      requestAnimationFrame(animate)

      const startingPoint =
        0.75 * Math.PI + (2.25 * Math.PI - 0.75 * Math.PI) * startingPercentage
      const endingPoint =
        0.75 * Math.PI + (2.25 * Math.PI - 0.75 * Math.PI) * percentage
      let angle = 0

      function animate() {
        if (!ctx || !canvas) return
        const now = performance.now()
        const delta = Math.min((now - start) / duration, 1)
        const maxAngle = endingPoint - startingPoint
        angle = delta * maxAngle
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        draw()
        //break the animation when angle hits the percentage
        if (
          (angle >= maxAngle && maxAngle > 0) ||
          (angle <= maxAngle && maxAngle < 0) ||
          maxAngle === 0
        ) {
          return
        }
        requestAnimationFrame(animate)
      }

      function displayText(
        text: string,
        x: number,
        y: number,
        color: string,
        fontSize: number,
        fontWeight: number = 100,
      ) {
        if (!ctx) return
        ctx.beginPath()
        ctx.font = `${fontWeight} ${fontSize * ratio}px Arial`
        ctx.fillStyle = color
        ctx.textAlign = 'center'
        ctx.fillText(text, x, y)
      }

      function draw() {
        if (!ctx) return
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
        const startX = ctx.canvas.width / 2
        const startY = ctx.canvas.height / 2
        let s = 0.75 * Math.PI
        let e = startingPoint + angle

        const drawTachometer = () => {
          if (!ctx) return
          const startX = ctx.canvas.width / 2
          const startY = ctx.canvas.height / 2
          ctx.beginPath()
          ctx.arc(
            startX,
            startY,
            (width / 2.5) * ratio,
            0.75 * Math.PI,
            2.25 * Math.PI,
          )
          ctx.lineWidth = lineWidth * ratio
          ctx.lineCap = 'round'
          ctx.strokeStyle = colors[barColor]
          ctx.shadowBlur = 0
          ctx.stroke()
        }

        const drawValueBar = () => {
          if (!ctx) return
          ctx.beginPath()
          ctx.strokeStyle = colors[valueColor]
          ctx.arc(startX, startY, (width / 2.5) * ratio, s, e, false)
          ctx.shadowBlur = 0
          ctx.stroke()
        }

        const drawValuePointCircle = () => {
          if (!ctx) return
          let x = startX + Math.cos(e) * (width / 2.5) * ratio
          let y = startY + Math.sin(e) * (width / 2.5) * ratio

          //create the circle indicator
          ctx.beginPath()
          ctx.arc(x, y, 10 * ratio, 0, 2 * Math.PI)
          ctx.fillStyle = colors[valueColor]
          ctx.fill()
          ctx.lineWidth = 10 * ratio
          ctx.strokeStyle = 'white'

          //adding a shadow on the circle indicator
          ctx.shadowColor = '#ccc'
          ctx.shadowBlur = 5
          ctx.shadowOffsetX = 0
          ctx.shadowOffsetY = 0
          ctx.stroke()
        }

        drawTachometer()
        drawValueBar()
        drawValuePointCircle()
        ctx.shadowBlur = 0 // ensure text is without blur
        secondaryText &&
          displayText(
            secondaryText,
            startX,
            startY - secondaryFontSize * ratio,
            '#64748B',
            secondaryFontSize,
          )
        primaryText &&
          displayText(
            primaryText,
            startX,
            startY + primaryFontSize * ratio,
            '#121A26',
            primaryFontSize,
          )
      }
    })
  }, [
    barColor,
    duration,
    lineWidth,
    percentage,
    primaryFontSize,
    primaryText,
    secondaryFontSize,
    secondaryText,
    startingPercentage,
    valueColor,
    width,
  ])
  return <canvas height={height} ref={ref} width={width} />
}
